/** English strings for the domains area. The Vietnamese file must have exactly the same keys. */
export const domains = {
  /** Status vocabularies, keyed by the backend enum code (shown as a tooltip next to the label). */
  status: {
    lifecycle: {
      DRAFT: 'Draft',
      AWAITING_DOSSIER: 'Awaiting dossier',
      AWAITING_PAYMENT: 'Awaiting payment',
      PENDING_CREATE: 'Pending create',
      CREATE_FAILED: 'Create failed',
      ACTIVE: 'Active',
      PENDING_RENEW: 'Pending renew',
      PENDING_TRANSFER: 'Pending transfer',
      EXPIRED: 'Expired',
      GRACE_PERIOD: 'Grace period',
      REDEMPTION: 'Redemption',
      PENDING_DELETE: 'Pending delete',
      DELETED: 'Deleted',
      TRANSFERRED_OUT: 'Transferred out',
    },
    compliance: {
      UNKNOWN: 'Unknown',
      COMPLIANT: 'Compliant',
      ACTION_REQUIRED: 'Action required',
      BLOCKED: 'Blocked',
      SUSPENDED: 'Suspended',
    },
    service: {
      NORMAL: 'Normal',
      DNS_SUSPENDED: 'DNS suspended',
      SUSPENDED: 'Suspended',
      LOCKED: 'Locked',
    },
    billing: {
      NONE: 'None',
      AWAITING_PAYMENT: 'Awaiting payment',
      RESERVED: 'Funds reserved',
      PAID: 'Paid',
      REFUND_PENDING: 'Refund pending',
      REFUNDED: 'Refunded',
      OVERDUE: 'Overdue',
    },
    dossier: {
      NOT_CREATED: 'Not created',
      COLLECTING: 'Collecting',
      PENDING_IDENTITY: 'Pending identity',
      PENDING_DOCUMENT: 'Pending document',
      PENDING_SIGNATURE: 'Pending signature',
      PENDING_REVIEW: 'Pending review',
      COMPLETE: 'Complete',
      INVALID: 'Invalid',
      EXPIRED: 'Expired',
      CORRECTION_REQUIRED: 'Correction required',
      SUPERSEDED: 'Superseded',
    },
    command: {
      PENDING: 'Pending',
      SENT: 'Sent',
      SUCCESS: 'Success',
      REJECTED: 'Rejected',
      UNKNOWN: 'Unknown',
      RECONCILING: 'Reconciling',
      RETRY_PENDING: 'Retry pending',
      MANUAL_REVIEW: 'Manual review',
    },
    policy: {
      ALLOW: 'Allow',
      ALLOW_WITH_REQUIRED_ACTIONS: 'Allow with actions',
      BLOCK: 'Block',
    },
    workflow: {
      RUNNING: 'Running',
      WAITING: 'Waiting',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
      MANUAL_REVIEW: 'Manual review',
      COMPENSATING: 'Compensating',
    },
    transfer: {
      REQUESTED: 'Requested',
      AUTHORIZED: 'Authorised',
      REGISTRY_PENDING: 'Pending at registry',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
      EXPIRED: 'Expired',
      COMPLETED: 'Completed',
    },
    abuse: {
      NEW: 'New',
      TRIAGED: 'Triaged',
      INVESTIGATING: 'Investigating',
      ACTION_REQUIRED: 'Action required',
      WAITING: 'Waiting',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
    dispute: {
      OPENED: 'Opened',
      LOCKED: 'Domain locked',
      DECIDED: 'Decided',
      IMPLEMENTED: 'Decision carried out',
      WITHDRAWN: 'Withdrawn',
      CLOSED: 'Closed',
    },
    vnnicSync: {
      NOT_SYNCED: 'Not synced',
      SYNC_PENDING: 'Waiting to sync',
      QUEUED: 'Queued',
      SUBMITTING: 'Sending',
      SUBMITTED: 'Sent, awaiting VNNIC',
      ACKNOWLEDGED: 'Accepted by VNNIC',
      REJECTED: 'Rejected by VNNIC',
      CORRECTION_REQUIRED: 'VNNIC asked for corrections',
      RESUBMIT_PENDING: 'Waiting to resend',
      SYNC_ERROR: 'Sync error',
      SUPERSEDED: 'Replaced by a newer dossier',
    },
    vnnicTx: {
      SUBMITTING: 'Sending',
      SUBMITTED: 'Sent',
      ACKNOWLEDGED: 'Accepted',
      REJECTED: 'Rejected',
      CORRECTION_REQUIRED: 'Corrections requested',
      FAILED: 'Failed',
      UNKNOWN: 'Outcome unknown',
    },
    /** Tooltip on every status badge. */
    code: 'Code: {{code}}',
  },

  /** Free-text codes shown as readable labels (unknown codes fall back to the code, humanised). */
  vocab: {
    evidence: { SIGNATURE_CERT: 'Digital signature on the declaration', DOCUMENT: 'Document', EKYC_RESULT: 'eKYC result', REVIEW_NOTE: 'Reviewer note', PROVIDER_RESULT: 'Provider result', EID_ASSERTION: 'Electronic identity' },
    evidenceField: { signer: 'Signer', signed_at: 'Signed', ca: 'Certificate authority', timestamp: 'Time-stamp', checks: 'Checks', ekyc_request_id: 'eKYC request', face_match_score: 'Face match', request_id: 'Request', declaration_id: 'Declaration' },
    eppStatus: {
      ok: 'OK',
      inactive: 'Inactive (no name servers)',
      clientHold: 'On hold (registrar)',
      serverHold: 'On hold (registry)',
      clientDeleteProhibited: 'Delete prohibited (registrar)',
      serverDeleteProhibited: 'Delete prohibited (registry)',
      clientRenewProhibited: 'Repeat-renewal block (24 hours after a renewal)',
      serverRenewProhibited: 'Renew prohibited (registry)',
      clientTransferProhibited: 'Transfer lock',
      serverTransferProhibited: 'Transfer prohibited (registry)',
      clientUpdateProhibited: 'Update lock (protects the contacts)',
      serverUpdateProhibited: 'Update prohibited (registry)',
      pendingCreate: 'Pending create',
      pendingDelete: 'Pending delete',
      pendingRenew: 'Pending renew',
      pendingTransfer: 'Pending transfer',
      pendingUpdate: 'Pending update',
      pendingRestore: 'Pending restore',
      redemptionPeriod: 'Redemption period',
      addPeriod: 'Add grace period',
      autoRenewPeriod: 'Auto-renew grace period',
      renewPeriod: 'Renew grace period',
      transferPeriod: 'Transfer grace period',
    },
    role: { REGISTRANT: 'Registrant', ADMIN: 'Administrative', TECH: 'Technical', BILLING: 'Billing' },
    direction: { IN: 'Transfer in', OUT: 'Transfer out' },
    actor: { OPERATOR: 'Staff', CUSTOMER: 'Customer', RESELLER: 'Reseller', SYSTEM: 'System', EXTERNAL: 'External party', REGISTRY: 'Registry' },
    tldClass: { VN: 'Vietnam ccTLD', VN_CCTLD: 'Vietnam ccTLD', GTLD: 'Generic TLD (gTLD)', NEW_GTLD: 'New gTLD', CCTLD: 'Country-code TLD (ccTLD)', OTHER: 'Other' },
    object: { DOMAIN: 'Domain', CONTACT: 'Contact', HOST: 'Host', POLL: 'Poll message' },
    workflow: {
      DOMAIN_REGISTRATION: 'Domain registration',
      DOMAIN_RENEWAL: 'Domain renewal',
      DOMAIN_UPDATE: 'Domain update',
      DOMAIN_EXPIRY: 'Expiry handling',
      DOMAIN_TRANSFER_IN: 'Transfer in',
      DOMAIN_TRANSFER_OUT: 'Transfer out',
      DOMAIN_REGISTRANT_CHANGE: 'Registrant change',
      VNNIC_SYNC: 'VNNIC dossier sync',
      REGISTRY_POLL: 'Registry poll',
    },
    lock: {
      TRANSFER_LOCK: 'Transfer lock',
      REGISTRAR_LOCK: 'Registrar lock',
      COMPLIANCE_LOCK: 'Compliance lock',
      LEGAL_LOCK: 'Legal lock',
      DISPUTE_LOCK: 'Dispute lock',
      ABUSE_LOCK: 'Abuse lock',
      UDRP_LOCK: 'UDRP lock',
      COURT_LOCK: 'Court order lock',
      REGISTRY_LOCK: 'Registry lock',
    },
    documentType: {
      BUSINESS_REGISTRATION: 'Business registration certificate',
      NATIONAL_ID_SCAN: 'ID document scan',
      SIGNED_DECLARATION: 'Signed declaration',
      EKYC_ID_CARD_FRONT: 'eKYC ID image, front',
      EKYC_ID_CARD_BACK: 'eKYC ID image, back',
      EKYC_SELFIE: 'eKYC portrait',
      NATIONAL_ID_CARD: 'National ID card',
      PASSPORT: 'Passport',
      BUSINESS_REGISTRATION_CERTIFICATE: 'Business registration certificate',
      REGISTRATION_DECLARATION: 'Registration declaration',
      ADDRESS_PROOF: 'Proof of address',
      AUTHORIZATION_LETTER: 'Letter of authorisation',
      REGISTRAR_SIGNED_FILE: 'Registrar-signed file',
      REGISTRAR_SIGNED_DECLARATION: 'Registrar-signed declaration',
      SIGNED_SUBJECT_DECLARATION: 'Signed registrant declaration',
      REGISTRATION_FORM: 'Domain declaration',
      DOMAIN_DECLARATION: 'Domain declaration',
      TRANSFEREE_DECLARATION: 'Transferee declaration',
      NEW_HOLDER_DECLARATION: 'New holder declaration',
      IDENTITY_CARD_FRONT: 'Identity card (front)',
      IDENTITY_CARD_BACK: 'Identity card (back)',
      REGISTRAR_CHANGE_FORM: 'Registrar change declaration',
      CHANGE_INFORMATION_REQUEST: 'Request to change domain information',
      SENSITIVE_KEYWORD_COMMITMENT: 'Sensitive keyword commitment',
      RETURN_REQUEST: 'Domain return request',
      RETURN_REQUEST_FORM: 'Domain return request (form)',
      GOV_VN_COMMITMENT: 'gov.vn explanation and commitment minutes',
      LOOKING_DOMAIN_FORM: 'Looking domain service declaration',
      NAME_VN_EXPLANATION: '.name.vn registration explanation',
      SIGNED_ORGANIZATION_INFO: 'Signed organisation information',
      REGISTRANT_NAME_CHANGE_REQUEST: 'Request to change the registrant name',
      NAME_CHANGE_DECISION: 'Name change decision',
      TRANSFER_TAX_COMMITMENT: 'Transfer tax commitment',
      TRANSFER_TAX_RECEIPT: 'Transfer tax receipt',
      TRANSFER_AGREEMENT: 'Domain transfer request',
      TRANSFER_APPROVAL: 'Domain transfer approval',
      DOMAIN_CERTIFICATE: 'Domain certificate',
      SERVICE_CONTRACT: 'Domain service contract',
      OTHER: 'Other',
    },
    purpose: {
      REGISTRATION_FORM: 'Domain registration form',
      DOMAIN_DECLARATION: 'Declaration generated for the domain',
      IDENTITY_CARD_FRONT: 'ID document (front)',
      IDENTITY_CARD_BACK: 'ID document (back)',
      EKYC_PORTRAIT: 'eKYC portrait',
      BUSINESS_REGISTRATION: 'Business registration certificate',
      REGISTRATION_DECLARATION: 'Registration declaration',
      IDENTITY_DOCUMENT: 'Identity document',
      IDENTITY_PROOF: 'Identity proof',
      ADDRESS_PROOF: 'Proof of address',
      AUTHORIZATION: 'Authorisation',
      REGISTRAR_SIGNED_FILE: 'Registrar-signed file',
      REGISTRAR_SIGNED_DECLARATION: 'Registrar-signed declaration',
      SIGNED_DECLARATION: 'Signed declaration',
      SIGNED_SUBJECT_DECLARATION: 'Signed registrant declaration',
      NATIONAL_ID_SCAN: 'ID document scan',
      EKYC_ID_CARD_FRONT: 'eKYC ID image, front',
      EKYC_ID_CARD_BACK: 'eKYC ID image, back',
      EKYC_SELFIE: 'eKYC portrait',
      TRANSFEREE_DECLARATION: 'Transferee declaration',
      NEW_HOLDER_DECLARATION: 'New holder declaration',
      REGISTRAR_CHANGE_FORM: 'Registrar change declaration',
      CHANGE_INFORMATION_REQUEST: 'Request to change domain information',
      SENSITIVE_KEYWORD_COMMITMENT: 'Sensitive keyword commitment',
      RETURN_REQUEST: 'Domain return request',
      RETURN_REQUEST_FORM: 'Domain return request (form)',
      GOV_VN_COMMITMENT: 'gov.vn explanation and commitment minutes',
      LOOKING_DOMAIN_FORM: 'Looking domain service declaration',
      NAME_VN_EXPLANATION: '.name.vn registration explanation',
      SIGNED_ORGANIZATION_INFO: 'Signed organisation information',
      REGISTRANT_NAME_CHANGE_REQUEST: 'Request to change the registrant name',
      NAME_CHANGE_DECISION: 'Name change decision',
      TRANSFER_TAX_COMMITMENT: 'Transfer tax commitment',
      TRANSFER_TAX_RECEIPT: 'Transfer tax receipt',
      TRANSFER_AGREEMENT: 'Domain transfer request',
      TRANSFER_APPROVAL: 'Domain transfer approval',
      DOMAIN_CERTIFICATE: 'Domain certificate',
      SERVICE_CONTRACT: 'Domain service contract',
      PASSPORT: 'Passport',
      OTHER: 'Other',
    },
    dossierType: {
      VN_DOMAIN: '.vn domain',
      INTERNATIONAL_GTLD: 'International gTLD',
      INTERNATIONAL_NEW_GTLD: 'International new gTLD',
      INTERNATIONAL_CCTLD: 'International ccTLD',
      OTHER: 'Other',
    },
    classification: { HIGHLY_SENSITIVE: 'Highly sensitive', CONFIDENTIAL: 'Confidential', INTERNAL: 'Internal', PUBLIC: 'Public' },
    check: { PASSED: 'Passed', PENDING: 'Pending', FAILED: 'Failed', REJECTED: 'Rejected', VERIFIED: 'Verified', EXPIRED: 'Expired' },
    method: {
      AUTH_INFO: 'AuthInfo code',
      EMAIL_LINK: 'E-mail confirmation link',
      REGISTRY_NOTICE: 'Notice from the registry',
      DUAL_CONFIRMATION: 'Confirmed by both registrants',
      BUSINESS_REGISTRY_LOOKUP: 'Business registry lookup',
      EKYC_CCCD_CHIP: 'eKYC with chip ID card',
      MANUAL_REVIEW: 'Manual review (removed)',
      DIGITAL_SIGNATURE: 'Digital signature',
      ELECTRONIC_IDENTITY: 'Electronic identity (VNeID)',
      EKYC: 'eKYC',
      LEGAL_DOCUMENT: 'Legal documents (removed)',
      SIGNED_DOCUMENT: 'Signed document',
      IN_PERSON: 'In person',
      PHONE_CALL: 'Phone call',
    },
    channel: { EPP_POLL: 'EPP poll message', VNNIC: 'VNNIC', PORTAL: 'Customer portal', CONTROL_CENTER: 'Control Center' },
    failure: {
      NEW_REGISTRANT_VERIFICATION_FAILED: 'The new registrant could not be verified',
      CANCELLED_BY_CUSTOMER: 'Cancelled by the customer',
      CONFIRMATION_EXPIRED: 'A confirmation was not given in time',
      REGISTRY_REJECTED: 'Rejected by the registry',
      // A transfer in (crates/lifecycle/src/transfer.rs)
      COMPLIANCE_BLOCKED: 'Blocked by a compliance check',
      TRANSFER_FAILED: 'The transfer in failed',
      TRANSFER_CANCELLED: 'The transfer request was cancelled',
      TRANSFER_REJECTED: 'The losing registrar rejected the transfer',
      // Registration, renewal, restore (crates/lifecycle): also the reason of the order item's refund
      FAILED: 'The operation failed',
      ABANDONED: 'Stopped before completion',
      CANCELLED_BY_STAFF: 'Cancelled by staff',
      REJECTED_BY_STAFF: 'Rejected by staff',
      NO_DOSSIER: 'No domain dossier',
      DOSSIER_INCOMPLETE: 'The domain dossier is incomplete',
      RESTORE_NOT_ALLOWED_BY_POLICY: 'The policy does not allow a restore',
      PROMO_CODE_REJECTED: 'VNNIC refused the promotion code',
      PROMO_CODE_REQUIRED: 'This zone requires a promotion code',
      PROMO_CODE_NOT_ELIGIBLE: 'This zone has no free program for the order',
      REGISTRANT_TYPE_NOT_ALLOWED: 'This type of registrant may not register the zone',
      REGISTRANT_NATIONALITY_NOT_ALLOWED: "A registrant of this nationality may not register the zone",
      MANUAL_ACTIVATION_REJECTED: 'Staff did not approve the activation this zone requires',
      // An order paid by bank transfer whose payment request ended unpaid (crates/ordering/src/pay_later.rs)
      PAYMENT_NOT_RECEIVED: 'Payment not received',
      // A sold service (crates/subscriptions): a delivery that definitely failed (or staff gave up), a renewal of a
      // service that has ended, a renewal the provider refused
      SERVICE_NOT_DELIVERED: 'The service could not be delivered',
      RENEWAL_NOT_APPLICABLE: 'The service has ended and cannot be renewed',
      RENEWAL_REFUSED: 'The provider refused to renew the service',
      PENDING_CREATE_REFUSED: 'The registry refused the registration after review',
      RENEWAL_NOT_APPLIED: 'The registry renewed the domain while it was no longer waiting for renewal; staff to check',
    },
    notice: {
      before_one: '{{count}} day before expiry',
      before_other: '{{count}} days before expiry',
      after_one: '{{count}} day after expiry',
      after_other: '{{count}} days after expiry',
      AUTO_RENEW_FAILED: 'Auto-renew failed',
    },
    // Order (`ordering.orders`) and order-item (`ordering.order_items`) statuses, both.
    orderStatus: {
      PLACED: 'Placed',
      PENDING: 'Pending',
      FUNDS_RESERVED: 'Funds reserved',
      FULFILLING: 'Fulfilling',
      PROCESSING: 'Processing',
      COMPLETED: 'Completed',
      PARTIALLY_COMPLETED: 'Partly completed',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
      FULFILLED: 'Fulfilled',
      REFUNDED: 'Refunded',
    },
    operation: {
      REGISTER: 'Registration',
      RENEW: 'Renewal',
      TRANSFER: 'Transfer',
      RESTORE: 'Restore',
      REDEMPTION: 'Redemption',
      CHANGE_REGISTRANT: 'Registrant change',
      CHANGE_NAMESERVERS: 'Nameserver change',
      DELETE: 'Deletion',
      VNNIC_SYNC: 'VNNIC dossier sync',
    },
    hold: { ACTIVE: 'Held', CAPTURED: 'Captured', RELEASED: 'Released', EXPIRED: 'Expired' },
    refund: { PENDING: 'Pending', COMPLETED: 'Completed', FAILED: 'Failed' },
    entry: {
      HOLD_CAPTURE: 'Payment captured',
      REGISTRY_COST_ACCRUAL: 'Registry cost',
      REVERSAL: 'Reversal',
      WALLET_TOPUP: 'Wallet top-up',
      REFUND: 'Refund',
      WALLET_ADJUSTMENT: 'Balance adjustment',
      WALLET_PAYOUT: 'Refund to the source',
    },
    invoice: { INVOICE: 'Invoice', CREDIT_NOTE: 'Credit note' },
    account: { ASSET: 'Asset', LIABILITY: 'Liability', REVENUE: 'Revenue', EXPENSE: 'Expense', EQUITY: 'Equity' },
    posting: { DEBIT: 'Debit', CREDIT: 'Credit' },
    /** `domain.domains.transfer_state`. */
    transferState: { NONE: 'No transfer in progress', PENDING_IN: 'Transfer in in progress', PENDING_OUT: 'Transfer out in progress' },
    /** `domain.domains.dns_state`: whether the delegation is signed with DNSSEC. */
    dnsState: { NONE: 'No DNS change recorded', DELEGATED: 'Delegated', SIGNED: 'Signed with DNSSEC', UNSIGNED: 'Not signed with DNSSEC' },
    /** EPP transfer status reported by the registry (trStatus). */
    trStatus: {
      pending: 'Pending at the registry',
      clientApproved: 'Approved by the losing registrar',
      clientCancelled: 'Cancelled by the gaining registrar',
      clientRejected: 'Rejected by the losing registrar',
      serverApproved: 'Approved by the registry',
      serverCancelled: 'Cancelled by the registry',
    },
    /** How the EPP gateway classified a failed or unanswered command. */
    errorClass: {
      TIMEOUT: 'No answer in time',
      TIMEOUT_BEFORE_SEND: 'Timed out before sending',
      TIMEOUT_AFTER_SEND: 'No answer in time after sending',
      CONNECTION_RESET: 'Connection dropped',
      CONNECTION_RESET_AFTER_SEND: 'Connection dropped after sending',
      CLOSED_BEFORE_SEND: 'Connection closed before sending',
      CONNECT_FAILED: 'Could not connect to the registry',
      TLS_FAILED: 'Secure connection failed',
      IO_BEFORE_SEND: 'Network error before sending',
      IO_AFTER_SEND: 'Network error after sending',
      MALFORMED_RESPONSE: 'Unreadable answer from the registry',
      FRAME_TOO_LARGE: 'Answer too large',
      LOGIN_REJECTED: 'Registry refused the login',
      TRID_MISMATCH: 'Answer did not match the command',
      SESSION_UNUSABLE: 'Registry session unusable',
      WORKER_LOST_AFTER_SEND: 'Processing stopped after sending',
      UNSUPPORTED_BY_REGISTRY: 'Not supported by the registry',
      REGISTRY_TIMEOUT: 'No answer from the registry in time',
    },
    /** Traffic class of a registry command (queue and rate limits). */
    trafficClass: { INTERACTIVE: 'Interactive (priority)', BACKGROUND: 'Background', POLL: 'Poll messages', DROPCATCH: 'Drop catching', STANDARD: 'Standard' },
    platformAccount: 'Platform',
  },

  common: {
    id: 'ID',
    current: 'Current',
    ended: 'Ended',
    since: 'since {{when}}',
    until: 'until {{when}}',
    none: 'None',
    unnamed: 'Name hidden',
    unnamedHint: 'Registrant names are only shown to staff who may read subjects.',
    noSubject: 'No linked subject',
    yes: 'Yes',
    no: 'No',
    loadFailed: 'Could not load this section',
    open: 'Open',
    version: 'v{{n}}',
  },

  list: {
    title: 'Domains',
    subtitle: 'Find a domain by name and check its lifecycle, dossier, compliance and expiry at a glance.',
    crumb: 'Business',
    name: 'Name starts with',
    namePlaceholder: 'e.g. example or ví-dụ',
    lifecycle: 'Lifecycle',
    any: 'Any state',
    search: 'Search',
    clear: 'Clear filters',
    advanced: 'Advanced filters',
    activeCount: 'Active filters: {{count}}',
    nSelected: '{{count}} selected',
    subjectSearch: 'Legal name of the subject',
    registry: 'Registry',
    registryPlaceholder: 'e.g. VNNIC',
    tld: 'Domain suffix',
    tldPlaceholder: 'e.g. vn, com.vn, com',
    autoRenew: 'Auto-renew',
    yes: 'On',
    no: 'Off',
    expires: 'Expiry',
    basis: 'Expiry by',
    rangeFrom: '{{label}} from',
    rangeTo: '{{label}} to',
    withinDays: 'Expires within {{days}} days',
    expiresBefore: 'Expires before {{date}}',
    tableTitle: 'Domain list',
    tableDescription: 'Matches the start of the name, in Unicode or Punycode. The most recently added first unless a column header is clicked to sort. Click a name to open the domain.',
    colDomain: 'Domain',
    colCreated: 'Added',
    colRegistered: 'Registered',
    colCustomer: 'Customer',
    colSubject: 'Subject',
    colLifecycle: 'Lifecycle',
    colDossier: 'Dossier',
    colCompliance: 'Compliance',
    colRegistryExpiry: 'Registry expiry',
    colCustomerExpiry: 'Customer expiry',
    differs: 'Differs',
    differsHint: 'Customer expiry differs from registry expiry',
    empty: 'No domains match',
    emptyFiltered: 'No domain matches the filters in use. Widen or clear the filters.',
    emptyAll: 'There are no domains yet.',
    emptyPage: 'This page is past the end of the list.',
    lastPage: 'Go to the last page',
    pageInfo: 'Page {{page}} of {{pages}} · {{total}} domains',
  },

  detail: {
    loading: 'Loading domain…',
    notFound: 'Domain not found',
    loadFailed: 'Could not load the domain',
    refresh: 'Refresh',
    sections: 'Domain sections',
    subtitle: 'Everything about one domain: status, registrant, registry, billing, compliance and history.',
  },

  upstream: {
    title: 'Verification at the upstream registrar',
    hint: 'This domain is sponsored by an upstream registrar (WebNIC): it e-mails the registrant to verify and suspends the domain when not verified. No verification e-mail of our own is sent.',
    attention: 'The registrant is not verified or the domain is suspended at the upstream registrar. A work item is open.',
    verified: 'Registrant verification',
    suspended: 'Suspended',
    privacy: 'WHOIS privacy (WebNIC)',
    checkedAt: 'Last read',
    resentAt: 'Verification e-mail resent',
    lastError: 'Last read error',
    yes: 'Verified',
    notVerified: 'Not verified',
    unknown: 'Unknown',
    suspendedYes: 'Suspended',
    no: 'No',
    notStated: 'Not stated',
    on: 'On',
    off: 'Off',
    notRead: 'Not read yet. Use "Read again" to fetch it from the upstream registrar.',
    resend: 'Resend verification e-mail',
    resendTitle: 'Resend the upstream registrar\'s verification e-mail',
    resendHint: 'WebNIC sends its verification link again to the registrant e-mail it holds.',
    resent: 'Sent to {{to}}.',
    refresh: 'Read again',
    refreshTitle: 'Read the verification / suspension state again',
    refreshed: 'Updated.',
  },
  actions: {
    menu: 'Actions',
    description: 'Sent to the registry now, as for a customer request. The reason is kept in the audit log with your name.',
    descriptions: {
      registryContacts: 'Read at the registry now, read only: nothing changes at the registry or here. The read is kept in the audit log.',
      email: "Sent from the template in force for the customer's brand. The reason is kept in the audit log and the domain history.",
    },
    items: {
      nameservers: 'Change name servers',
      dnssec: 'DNSSEC (DS records)',
      lock: 'Transfer lock',
      transferCode: 'New transfer code',
      updateLock: 'Update lock',
      renew: 'Renew',
      registrant: 'Change registrant',
      nameChange: 'Name change / holder transfer (.vn)',
      restore: 'Restore the domain',
      delete: 'Delete domain',
      services: 'Domain services (add-ons)',
      hold: 'Hold / release the domain',
      registryContacts: 'Contacts at the registry',
      email: 'Send an e-mail',
    },
    related: {
      title: 'Related',
      customer: 'Customer',
      subject: 'Subject (registrant)',
      dossier: 'Dossier',
      orders: 'Orders',
      invoices: 'Invoices',
      transfers: 'Transfers',
      registryTx: 'Registry transactions',
      work: 'Work to do',
      history: 'History',
    },
    reason: 'Reason',
    reasonHint: 'Why you are doing this (kept in the audit log).',
    close: 'Close',
    choose: '— Choose —',
    loading: 'Loading…',
    applied: 'Done: the registry applied the change.',
    pending: 'Sent: the registry has not confirmed yet; the result will show on the domain.',
    ns: {
      label: 'Name servers (one per line, 2 to 13)',
      hint: '{{count}} name server(s)',
      submit: 'Save name servers',
      glueTitle: 'Addresses sent with the name servers (glue)',
      glueHint:
        'Only name servers under this domain (such as ns1.{{domain}}) are sent with IP addresses, and each needs at least one IPv4 or IPv6 address; separate several with commas. Other name servers are sent by name only.',
      ipv4: 'IPv4',
      ipv6: 'IPv6',
      ipv4Placeholder: '192.0.2.1',
      ipv6Placeholder: '2001:db8::1',
      external: 'Sent by name only, without IP: {{list}}',
      notIpv4: 'Not an IPv4 address: {{list}}',
      notIpv6: 'Not an IPv6 address: {{list}}',
      glueMissing: 'Every name server under the domain needs at least one address.',
    },
    ds: {
      none: 'No DS record: DNSSEC is off.',
      unsupported: 'This registry does not announce DNSSEC support.',
      keyTag: 'Key tag',
      algorithm: 'Algorithm',
      digestType: 'Digest type',
      digest: 'Digest',
      publicKey: 'Public key (DNSKEY)',
      publicKeyHint: 'Required by VNNIC for .vn; optional elsewhere.',
      add: 'Add a DS record',
      remove: 'Remove',
      submit: 'Save DNSSEC',
      turnOff: 'Turn DNSSEC off',
    },
    lock: {
      nowLocked: 'The domain is locked against transfer.',
      nowUnlocked: 'The domain is not locked against transfer.',
      lock: 'Lock',
      unlock: 'Unlock',
      heldUntil: 'The transfer policy lock (after registration, a transfer in or a change of registrant) holds until {{until}}: it cannot be unlocked before then.',
      held: 'The transfer policy lock is in force: it cannot be unlocked.',
    },
    updateLock: {
      nowLocked: 'The domain is update-locked (clientUpdateProhibited): the registry refuses every change until it is removed.',
      nowUnlocked: 'The domain is not update-locked.',
      hint: 'Set after every contact change; the platform removes and sets it again around its own changes (name servers, DNSSEC, transfer lock, renewal).',
      lock: 'Lock updates',
      unlock: 'Unlock updates',
    },
    hold: {
      nowHeld: 'The domain is on hold (clientHold): the registry does not publish it in DNS.',
      nowFree: 'The domain is not on hold (no clientHold).',
      hint: 'A hold sets clientHold at the registry: the domain stops resolving until it is released. A hold for an unverified e-mail (ICANN) or an abuse case is released where it was placed.',
      hold: 'Hold the domain',
      release: 'Release the domain',
    },
    quick: {
      title: 'Quick actions',
      sync: 'Sync with registry',
      syncBusy: 'Reading at the registry…',
      synced: "Read at the registry at {{time}}; the page shows the registry's answer.",
    },
    registryContacts: {
      hint: 'domain:info, then contact:info of each contact, next to the contacts here; personal identity numbers are masked, rows that differ are highlighted.',
      readAt: 'Read at {{time}}.',
      reload: 'Read again',
      field: 'Field',
      atRegistry: 'At the registry',
      inSystem: 'Here',
      sameHandle: 'Same contact ID as here',
      otherHandle: 'Another contact ID is recorded here: {{handle}}',
      notOurs: 'Held by another registrar',
      reads: {
        NOT_FOUND: 'The registry no longer holds this contact.',
        UNSUPPORTED: 'This connection does not read contacts at the registry.',
        UNAVAILABLE: 'The contact could not be read now ({{outcome}}).',
        NONE: 'The registry names no contact for this role.',
      },
      fields: { name: 'Name', organization: 'Organisation', email: 'E-mail', phone: 'Phone', fax: 'Fax', address: 'Address', identity: 'Identity number' },
    },
    email: {
      template: 'Template',
      templates: {
        verifyEmail: 'Resend the verification e-mail (ICANN)',
        unverifiedSuspended: 'Suspension notice for an unverified e-mail, with the verification link (ICANN)',
        dataReminder: 'Registration data reminder (ICANN WDRP)',
        expiryReminder: 'Expiry reminder (ICANN ERRP)',
        expiredNotice: 'Notice that the domain expired (ICANN ERRP)',
      },
      unavailable: {
        ICANN_ONLY: 'ICANN domains only',
        ALREADY_VERIFIED: "the registrant's e-mail is verified",
        NO_EMAIL: 'the registrant has no e-mail',
        NOT_LIVE: 'the domain is no longer at the registry',
      },
      actual: 'The e-mail sent: {{template}}.',
      upstream: 'The upstream registrar (WebNIC) sends its own verification e-mail to the address it holds; the platform asks it to send it again.',
      to: 'To',
      subject: 'Subject',
      preview: 'E-mail preview',
      linkNote: 'The one-time verification link is made when it is sent.',
      submit: 'Send the e-mail',
      queued: 'Queued to {{to}}; the domain history records it.',
      sent: 'The upstream registrar was asked to send it again to {{to}}.',
    },
    code: {
      warning: 'The current transfer code stops working. Give the new code only to the registrant.',
      code: 'New transfer code',
      once: 'Shown only now; it is not kept in readable form.',
      sent: 'The registry e-mailed the new code to the registrant ({{recipient}}).',
      submit: 'Issue a new code',
    },
    restore: {
      price: 'Restore fee {{total}} · charged to: {{customer}}',
      started: 'Restore ordered; the registry result will show on the domain shortly.',
      submit: 'Restore',
    },
    renew: {
      years: 'Period',
      yearsN_one: '{{count}} year',
      yearsN_other: '{{count}} years',
      price: 'Total {{total}} · charged to: {{customer}}',
      started: 'Renewal ordered; the registry result will show on the domain shortly.',
      submit: 'Renew',
      recent: 'Renewing now adds {{count}} more year(s) to the new expiry.',
      confirm: 'I confirm renewing again although the domain was just renewed',
      confirmHint: 'A reason is required below; renewing within the 24-hour limit is recorded in the audit log and the domain history.',
      submitAnyway: 'Renew again',
    },
    registrant: {
      nameChange: 'The new registrant has another legal name: VNNIC changes it only in its panel, so a name-change request was opened (documents, review).',
      openNameChange: 'Open the request',
      to: 'New registrant',
      toHint: 'A subject of the same customer. Create one on the Subjects page first if needed.',
      optOut: 'The registrant waives the transfer lock that follows the change (only where the policy allows it)',
      submit: 'Start the change',
      started: 'Change started. Record how each side agreed:',
      startedByEmail: 'Change started. The current and the new registrant each get an e-mail and agree with the link in it; the change goes to the registry once both have agreed.',
      method: 'How consent was obtained',
      methods: { IN_PERSON: 'In person', SIGNED_DOCUMENT: 'Signed document', DIGITAL_SIGNATURE: 'Digital signature', EMAIL_LINK: 'E-mail confirmation', PHONE_CALL: 'Phone call' },
      confirmCurrentRegistrant: 'Current registrant agreed',
      confirmNewRegistrant: 'New registrant agreed',
      authorized: 'Both sides agreed: the change goes to the registry.',
      contactTitle: 'New registrant at the registry',
      contactHint: 'The contact ID sent to the registry for this subject. The change creates it when it reaches the registry; created here first, you have its ID now and the change uses that contact.',
      contactCreated: 'created at the registry',
      contactNotCreated: 'not created at the registry yet',
      createContact: 'Create the new registrant',
      contactReason: 'Give the reason above first.',
      handleSent: 'Registrant contact sent to the registry: {{handle}}',
    },
    delete: {
      warning: 'The domain is deleted at the registry. Some registries allow restoring it for a short time; VNNIC does not.',
      type: 'Type {{name}} to confirm',
      submit: 'Delete',
    },
  },

  tabs: {
    overview: 'Overview',
    lifecycle: 'Lifecycle',
    subject: 'Subject',
    dossier: 'Dossier',
    documents: 'Documents',
    registry: 'Registry',
    epp: 'EPP',
    contacts: 'Contacts',
    nameservers: 'Nameservers',
    dnssec: 'DNSSEC',
    orders: 'Orders',
    billing: 'Billing',
    invoices: 'Invoices',
    transfers: 'Transfers',
    compliance: 'Compliance',
    vnnic: 'VNNIC',
    history: 'History',
    audit: 'Audit',
    observability: 'Observability',
    services: 'Services',
  },

  summary: {
    registrationStopped: 'Registration stopped',
    label: 'Domain summary',
    domain: 'Domain',
    aLabel: 'A-label',
    lifecycle: 'Lifecycle',
    noStatuses: 'no registry statuses',
    registry: 'Registry',
    expires: 'Expiry',
    registryExpiry: 'Registry',
    customerExpiry: 'Customer',
    notSet: 'not set',
    behind: 'Customer {{days}} days behind registry',
    ahead: 'Customer {{days}} days ahead of registry',
    customer: 'Customer',
    subject: 'Registrant',
    none: 'None',
    dossier: 'Dossier',
    documents: 'v{{version}} · {{count}} document(s)',
    compliance: 'Compliance',
    locks: '{{count}} active lock(s)',
    service: 'Service',
    autoRenew: 'Auto renew',
    autoRenewOn: 'On · {{years}}y',
    off: 'Off',
    billing: 'Billing',
  },

  stop: {
    incompleteTitle: 'The registry would refuse this subject’s data. Complete it before ordering:',
    verificationTitle: 'The subject is not verified: the order can be placed, but the domain waits in "Pending identity" until the subject is verified.',
    title: 'Registration stopped',
    refused: 'Refused command:',
    notSent: 'not sent to the registry',
    vnnicReason: 'Why VNNIC did not issue the promotion code:',
    todo: 'To do',
    editSubject: 'Edit the subject',
    editRepresentative: 'Edit the representative',
    subjectChanged: 'The subject has new data. Retry to use it (the dossier is made again for the new version).',
    held: 'Funds held:',
    workItem: 'Open the work item',
    waitingTitle: 'Registration waiting for its dossier',
    reusable: 'The subject already has a valid verified identity ({{method}}, {{when}}). Retry attaches it to the dossier and checks again at once.',
    retryWaitingBody: 'The domain dossier is checked again at once. If the subject already has a valid verified identity, it is attached to the dossier; if the subject was corrected, the dossier is rebuilt from the new data.',
    retry: 'Retry',
    cancel: 'Cancel registration',
    retryTitle: 'Retry the registration',
    retryBody: 'The registration runs again. When the subject was corrected, the dossier is made again from the new data before anything is sent.',
    cancelTitle: 'Cancel the registration',
    cancelBody: 'The order is cancelled and the funds held are released to the customer. This cannot be undone.',
    cancelBodyNothingHeld: 'The order is cancelled. This cannot be undone.',
    reason: 'Reason',
    back: 'Back',
    retried: 'The registration runs again.',
    cancelled: 'Registration cancelled; the funds held are released.',
    cancelledNothingHeld: 'Registration cancelled.',
    failed: 'Could not complete the action.',
    openOverview: 'See what to do',
    failedTitle: 'Registration not completed',
    reactivate: 'Reactivate the order',
    reactivateTitle: 'Reactivate the registration order',
    reactivateBody:
      'This order and domain go through the registration again (dossier, checks, contacts, domain create); no new order is made. The customer’s wallet holds {{amount}} again (available now: {{available}}); it is charged only when the domain is created, and released if the registration fails again.',
    reactivateHolds: 'Reactivating holds:',
    walletAvailable: 'Customer wallet available:',
    reactivateShort: 'The customer’s wallet cannot cover the order again: the customer needs to top up before it is reactivated.',
    reactivated: 'Order reactivated; the registration runs again from its dossier step.',
  },

  overview: {
    registrationFailed: 'Registration failed: {{reason}}',
    attention: 'Needs attention',
    attentionDescription: 'What to look at first, derived from the domain record.',
    action: 'Action',
    check: 'Check',
    nothing: 'Nothing needs attention.',
    txFailed: 'Registry commands could not be loaded; unresolved outcomes are not checked.',
    unresolved: '{{count}} registry command(s) without a final outcome, awaiting reconciliation. Do not retry manually.',
    pendingOrder: 'The upstream registrar is processing {{count}} order(s) (pending order); followed automatically until settled. Do not resend by hand.',
    service: 'Service is {{state}}.',
    compliance: 'Compliance: {{state}}.',
    locks: '{{count}} legal/compliance lock(s): {{list}}.',
    dossier: 'Dossier: {{state}}.',
    overdue: 'Billing overdue.',
    paidThrough: 'Customer paid through {{customer}} but registry expiry is {{registry}}.',
    expiresNoRenew: 'Expires {{when}} and auto-renew is off.',
    lifecycle: 'Lifecycle is {{state}}.',
    openRegistry: 'Open registry',
    openCompliance: 'Open compliance',
    openLocks: 'Open locks',
    openDossier: 'Open dossier',
    openBilling: 'Open billing',
    openLifecycle: 'Open lifecycle',
    openHistory: 'Open history',
    transferPending: 'A transfer to another registrar is in progress.',
    openTransfers: 'Open transfers',
    record: 'Who and where',
    domainId: 'Domain ID',
    customer: 'Customer',
    subject: 'Registrant',
    registry: 'Registry',
    tld: 'TLD',
    created: 'Created',
    updated: 'Updated',
    trace: 'Trace it',
    traceBody: 'Move from a domain event to its workflow, registry command, trace and logs without copying IDs.',
    historyChain: 'History (hash chain)',
    registryTxs: 'Registry commands',
    observabilityLinks: 'Observability links',
  },

  lifecycle: {
    state: 'State',
    stateDescription: 'Where the domain is on the standard lifecycle path.',
    pathLabel: 'Lifecycle',
    offPath: 'Off the standard path:',
    changed: 'Lifecycle changed',
    transferState: 'Transfer state',
    dnsState: 'DNS state',
    recordVersion: 'Record version',
    keyDates: 'Key dates',
    createdRegistrar: 'Created (registrar)',
    createdRegistry: 'Created (registry)',
    registryExpiry: 'Registry expiry',
    paidThrough: 'Customer paid through',
    autoRenew: 'Auto renew',
    autoRenewOn: 'On, {{years}} year(s)',
    off: 'Off',
    authInfo: 'AuthInfo set',
    note: 'Registry expiry is what the registry holds. Customer expiry is what the customer has paid for. They differ after a registry auto-renew that has not been billed, or a renewal that is paid but not yet confirmed by the registry.',
  },

  subjectTab: {
    none: 'No subject is bound to this domain',
    noneBody: 'The registrant (subject) is bound when the dossier is created. Check the Dossier tab.',
  },

  dossier: {
    origin: 'Origin',
    copiedFrom: 'Copied from the subject’s verified dossier on {{date}}',
    copiedFromVerification: '(verification evidence)',
    copiedFromSubject: '(subject document)',
    ownDocument: 'The domain’s own document',
    verificationCopy: 'Verification in this dossier',
    verificationCopyBody: 'The subject’s identity verification as this dossier took it; later changes to the subject do not change the dossier.',
    method: 'Method',
    result: 'Result',
    verifiedAt: 'Verified',
    fromSubject: 'Subject',
    openSubjectVerification: 'Open the subject’s verification and documents',
    copiedAt: 'Copied',
    vnnicFiles: 'Files for VNNIC',
    vnnicFilesBody: 'Dossier "{{docType}}", certification "{{method}}": the files VNNIC HSTM-API v1.3 Appendix 2 requires, with each file\'s signed status. The dossier is "Complete" only with exactly these files.',
    fileTypeNone: 'Not a VNNIC file type',
    fileTypes: { 1: 'ID card, front', 2: 'ID card, back', 3: 'Passport', 4: 'Business licence', 5: 'Domain registration declaration', 6: 'gov.vn explanation and commitment', 7: 'Sensitive keyword commitment', 8: 'Request to change the registrant name', 9: 'Name change decision', 10: 'Transfer tax commitment', 11: 'Transfer tax payment receipt', 12: 'Domain transfer document', 13: 'Domain return request', 14: 'Information change request', 15: 'Registrar change declaration', 16: 'Organisation registration information signed digitally' },
    signedStatus: { 0: 'not signed', 1: 'signed by the registrant', 2: 'signed by the registrar' },
    hstmSent: 'sent',
    hstmRejected: 'Not usable (VNNIC file rule)',
    hstmErrors: 'Cannot be sent to VNNIC yet',
    hstmComplete: 'All Appendix 2 files present',
    certifyMethods: { 1: 'Account verified by eKYC', 2: 'Account verified by digital signature', 3: 'Domain verified by eKYC', 4: 'Domain verified by digital signature', 5: 'Verified manually' },
    required: 'Required',
    notUploaded: 'kept in the dossier, not sent',
    missing: 'Missing',
    none: 'No dossier',
    noneBody: 'Every managed domain needs a dossier. The domain’s dossier state is',
    title: 'Dossier',
    status: 'Status',
    type: 'Type',
    id: 'Dossier ID',
    version: 'Version',
    subject: 'Registrant',
    subjectVersion: 'Registrant version',
    verification: 'Identity verification',
    policy: 'Policy',
    completed: 'Completed',
    openDossier: 'Open the dossier page',
    documents: 'Documents bound to the dossier',
    document: 'Document',
    purpose: 'Purpose',
    bound: 'Bound',
    noDocuments: 'No documents bound',
    noDocumentsBody: 'Documents are attached from the customer portal or the Documents page.',
  },

  documents: {
    title: 'Documents of every dossier version',
    description: 'Documents attached to any version of this domain’s dossier. File contents open only on the document page, and every view is recorded.',
    loadFailed: 'Could not load the documents',
    empty: 'No documents',
    emptyBody: 'Nothing has been attached to this domain’s dossier yet.',
    document: 'Document',
    purpose: 'Purpose',
    dossier: 'Dossier',
    dossierVersion: 'Version {{n}}',
    file: 'File',
    version: 'Version',
    notCurrent: 'superseded',
    verification: 'Check',
    uploaded: 'Uploaded',
    expires: 'expires {{date}}',
  },

  registry: {
    lastRefusal: 'Latest command',
    noRefusal: 'No command sent yet',
    object: 'Registry object',
    registry: 'Registry',
    tld: 'TLD',
    statuses: 'Registry statuses',
    statusSource: '{{source}}, observed {{at}}',
    none: 'None',
    expiry: 'Registry expiry',
    unresolved: 'Unresolved outcomes',
    unresolvedCount: '{{count}} awaiting reconciliation. Do not retry manually.',
    locks: 'Active locks',
    lock: 'Lock',
    reason: 'Reason',
    case: 'Case',
    applied: 'Applied',
    autoRelease: 'Auto release',
    txs: 'Registry commands for this domain',
    txsDescription: 'Newest first. Open a command for its identifiers, outcome and follow-up links.',
    txsFailed: 'Could not load the registry commands',
    noTxs: 'No registry commands',
    noTxsBody: 'EPP commands sent for this domain will appear here.',
    started: 'Started',
    operation: 'Command',
    state: 'State',
    trids: 'clTRID / svTRID',
    noSvtrid: 'no svTRID',
    result: 'Result',
    latency: 'Latency',
    workflow: 'Workflow',
    attempt: 'attempt {{n}}',
    correlation: 'Correlation',
  },

  epp: {
    title: 'Registry commands',
    description: 'Every command sent to the registry for this domain and for the contacts and hosts it uses, with the registry’s result and message. Open a command for details.',
    loadFailed: 'Could not load the EPP commands',
    operation: 'Command',
    anyOperation: 'Any command',
    state: 'State',
    anyState: 'Any state',
    object: 'Object',
    anyObject: 'Any object',
    trid: 'clTRID or svTRID',
    tridPlaceholder: 'Exact transaction ID',
    search: 'Filter',
    empty: 'No EPP commands',
    emptyFiltered: 'No command matches these filters.',
    emptyAll: 'No command has been sent for this domain yet.',
    started: 'Started',
    command: 'Command',
    result: 'Result',
    trids: 'clTRID / svTRID',
    latency: 'Latency',
    workflow: 'Workflow',
    shown: '{{count}} shown',
  },

  contacts: {
    data: {
      title: 'Contact details',
      description: 'What the registry receives for this contact, from the subject version it was built from. Identity numbers are masked; open the subject to reveal (audited).',
      hidden: 'You may not read registrant data.',
      name: 'Name',
      organization: 'Organisation',
      tradeName: 'Trade name',
      kind: 'Kind',
      email: 'E-mail',
      phone: 'Phone',
      fax: 'Fax',
      gender: 'Gender',
      genders: { Male: 'Male', Female: 'Female' },
      position: 'Position',
      nationality: 'Nationality',
      birthDate: 'Date of birth',
      onFile: 'On file',
      identity: 'Identity document',
      address: 'Address',
      province: 'Province / city',
      ward: 'Ward / commune',
    },
    tabTitle: 'Contacts at the registry',
    tabDescription: 'This domain’s own four contacts (registrant, admin, tech, billing), cloned from the subject. Editing here changes this domain only.',
    thin: 'This registry keeps no contact objects; these contacts are held by the registrar only.',
    loadFailed: 'Could not load the contacts',
    empty: 'No contacts',
    emptyBody: 'Contacts are assigned when the domain is registered.',
    role: 'Role',
    contact: 'Contact',
    noHandle: 'Registrar only',
    subject: 'Belongs to',
    period: 'Assigned',
    usage: 'Domains using it',
    usedBy_one: '{{count}} domain',
    usedBy_other: '{{count}} domains',
    registrarContact: 'Registrar’s own contact',
    provenance: 'Source',
    origins: { CLONED: 'Cloned from the subject', SYNCED: 'Synced from the subject', EDITED: 'Edited for this domain' },
    clonedAt: 'Cloned {{date}}',
    updatedAt: 'Updated {{date}}',
    differs: 'Differs from the subject now: {{fields}}',
    matches: 'Matches the subject now',
    fields: { legal_name: 'name', identifier: 'ID number', address: 'address', email: 'e-mail', phone: 'phone', fax: 'fax', details: 'other details' },
    edit: 'Edit',
    editTitle: 'Edit contact: {{role}}',
    editDescription: 'Only this contact of {{domain}} changes; other domains stay as they are.',
    editSubject: 'Take the details of subject',
    editSubjectHint: 'The contact takes the chosen subject’s current details.',
    editSubmit: 'Update contact',
    editStarted: 'Updating the contact at the registry. Reload this section to see the result.',
    registrantHint: 'The registrant is changed through Actions › Change registrant.',

    title: 'Contacts',
    subtitle: 'Contact objects held at the registries for the domains we manage, and who they belong to.',
    crumb: 'Business',
    filterQuery: 'Contact ID or name',
    filterPlaceholder: 'e.g. TINO-C or Nguyễn',
    filterRole: 'Current role',
    anyRole: 'Any role',
    filterRegistry: 'Registry',
    anyRegistry: 'Any registry',
    search: 'Search',
    tableTitle: 'Contact objects',
    tableDescription: 'Sorted by registry and contact ID. Personal details stay masked; open a contact for the domains that use it.',
    colContact: 'Contact',
    colRegistry: 'Registry',
    colSubject: 'Belongs to',
    colRoles: 'Current roles',
    colDomains: 'Domains',
    colAssigned: 'Last assigned',
    noRoles: 'Not in use',
    totalDomains: '{{count}} in total',
    emptyList: 'No contacts match',
    emptyListBody: 'Try a shorter contact ID, another name or reset the filters.',
    shown: '{{count}} shown on this page',

    detailTitle: 'Contact {{handle}}',
    detailSubtitle: 'A contact object at {{registry}}: who it belongs to, the domains that use it and the commands sent for it.',
    notFound: 'Contact not found',
    loadDetailFailed: 'Could not load the contact',
    object: 'Contact object',
    handle: 'Contact ID',
    registry: 'Registry',
    roles: 'Current roles',
    domainCount: 'Domains now',
    firstUsed: 'First used',
    lastAssigned: 'Last assigned',
    behind: 'Belongs to',
    behindDescription: 'The registrant version the contact was created from. Identity numbers and contact details stay masked on the subject page.',
    subjectName: 'Name',
    subjectType: 'Type',
    subjectVersion: 'Version',
    notCurrentVersion: 'an older version',
    verification: 'Verification',
    country: 'Country',
    managingCustomer: 'Managing customer',
    noSubjectBody: 'This contact is not linked to a registrant; it is the registrar’s own contact.',
    domains: 'Domains using this contact',
    domain: 'Domain',
    customer: 'Customer',
    lifecycle: 'Lifecycle',
    expiry: 'Registry expiry',
    commands: 'Registry commands for this contact',
    commandsHidden: 'You may not view registry commands.',
    noCommands: 'No registry commands for this contact',
  },

  delegation: {
    loadFailed: 'Could not load the delegation',
    nsTitle: 'Nameservers',
    nsDescription: 'Where the domain is delegated. Glue addresses are kept only for hosts under the domain itself.',
    dnsState: 'DNS state',
    host: 'Host',
    glue: 'Glue addresses',
    noGlue: 'not needed',
    inBailiwick: 'Under this domain',
    external: 'External',
    updated: 'Updated',
    noNs: 'No nameservers',
    noNsBody: 'The domain is not delegated. Nameservers are set from the customer portal or by support.',
    dsTitle: 'DS records',
    dsDescription: 'Delegation signer records published at the registry for this domain.',
    signed: 'Signed with DNSSEC',
    unsigned: 'Not signed',
    notSupported: 'The registry does not accept DNSSEC for this TLD.',
    keyTag: 'Key tag',
    algorithm: 'Algorithm',
    digestType: 'Digest type',
    digest: 'Digest',
    noDs: 'No DS records',
    noDsBody: 'DNSSEC is not enabled for this domain.',
  },

  orders: {
    title: 'Orders',
    description: 'Order items that registered, renewed or transferred this domain.',
    loadFailed: 'Could not load the orders',
    empty: 'No orders',
    emptyBody: 'No order item for this domain has been placed through this platform.',
    order: 'Order',
    operation: 'Operation',
    years_one: '{{count}} year',
    years_other: '{{count}} years',
    amount: 'Amount',
    status: 'Status',
    hold: 'Funds',
    invoice: 'Invoice',
    customer: 'Customer',
    noInvoice: 'not invoiced',
  },

  billing: {
    title: 'Billing',
    description: 'What the customer pays for this domain, and every money movement linked to it.',
    loadFailed: 'Could not load billing',
    state: 'Billing state',
    customer: 'Billed to',
    currency: 'Billing currency',
    paidThrough: 'Paid through',
    registryExpiry: 'Registry expiry',
    autoRenew: 'Auto renew',
    autoRenewOn: 'On, {{years}} year(s)',
    off: 'Off',
    holds: 'Balance holds',
    holdsDescription: 'Funds reserved on the customer’s wallet while an order item is fulfilled.',
    noHolds: 'No balance holds',
    purpose: 'Purpose',
    amount: 'Amount',
    status: 'Status',
    created: 'Created',
    closed: 'Closed',
    expires: 'Expires',
    refunds: 'Refunds',
    noRefunds: 'No refunds',
    reason: 'Reason',
    journal: 'Ledger entries',
    journalDescription: 'Double-entry postings for this domain, newest first.',
    noJournal: 'No ledger entries',
    entry: 'Entry',
    posted: 'Posted',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    reverses: 'reverses an earlier entry',
    openEntry: 'Open entry',
  },

  invoices: {
    title: 'Invoices',
    description: 'Invoices and credit notes that bill this domain. Only the lines for this domain are shown.',
    loadFailed: 'Could not load the invoices',
    empty: 'No invoices',
    emptyBody: 'Nothing has been invoiced for this domain yet.',
    number: 'Number',
    type: 'Type',
    credits: 'corrects {{label}}',
    customer: 'Customer',
    issued: 'Issued',
    total: 'Invoice total',
    lines: 'Lines for this domain',
    line: 'Line',
    quantity: 'Qty',
    unitPrice: 'Unit price',
    tax: 'Tax',
    amount: 'Amount',
  },

  transfersTab: {
    state: 'Transfer situation',
    stateDescription: 'Whether the domain can move to another registrar right now.',
    transferState: 'Transfer state',
    authInfo: 'AuthInfo last set',
    noAuthInfo: 'never',
    activeLocks: 'Active transfer locks',
    noActiveLocks: 'None',
    transfers: 'Transfers',
    noTransfers: 'No transfers',
    noTransfersBody: 'The domain has never been transferred in or out.',
    changes: 'Registrant changes',
    noChanges: 'No registrant changes',
    locks: 'Transfer and registrar locks',
    locksDescription: 'Including released locks.',
    noLocks: 'No transfer locks',
    lock: 'Lock',
    reason: 'Reason',
    appliedBy: 'Applied by',
    applied: 'Applied',
    released: 'Released',
    active: 'Active',
    autoRelease: 'releases {{when}}',
    notices: 'Expiry notices sent',
    noNotices: 'No expiry notices sent',
    notice: 'Notice',
    expiry: 'About the expiry of',
    sent: 'Sent',
  },

  transfers: {
    title: 'Transfers',
    subtitle: 'Domains moving to or from another registrar, and where each transfer stands.',
    crumb: 'Business',
    filterDomain: 'Domain starts with',
    filterDomainPlaceholder: 'e.g. example',
    filterDirection: 'Direction',
    anyDirection: 'In and out',
    filterStatus: 'Status',
    anyStatus: 'Any status',
    search: 'Search',
    tableTitle: 'Transfers',
    tableDescription: 'Newest first. Click a domain to open the transfer: its authorisation, registry command and history.',
    colDomain: 'Domain',
    colDirection: 'Direction',
    colStatus: 'Status',
    colOther: 'Other registrar',
    colCustomer: 'Customer',
    colRequested: 'Requested',
    colNext: 'Deadline / decided',
    autoApprove: 'auto-approves {{when}}',
    decided: 'decided {{when}}',
    completed: 'completed {{when}}',
    empty: 'No transfers match',
    emptyBody: 'Try another status or direction, or reset the filters.',
    shown: '{{count}} shown on this page',

    detailTitle: '{{direction}}: {{domain}}',
    detailSubtitle: 'One transfer of {{domain}}: its status, authorisation, registry command and related history.',
    notFound: 'Transfer not found',
    loadFailed: 'Could not load the transfer',
    overview: 'Transfer',
    domain: 'Domain',
    direction: 'Direction',
    status: 'Status',
    registryStatus: 'Registry status',
    cancel: 'Cancel transfer',
    cancelTitle: 'Cancel this transfer request',
    cancelDescription: 'Withdraws our request at the registry. The order is released once the registry confirms.',
    cancelSubmit: 'Cancel the transfer',
    cancelled: 'Cancelled at the registry: the order will be released.',
    cancelPending: 'Sent: the registry has not confirmed the cancellation yet.',
    approveOut: 'Approve the transfer out',
    approveOutTitle: 'Let the domain move to another registrar',
    approveOutDescription: 'The domain leaves the platform once the registry completes the transfer. Approve only when the registrant has confirmed the move.',
    approveOutSubmit: 'Approve the transfer',
    approvedOut: 'Approved at the registry: the domain will be transferred.',
    rejectOut: 'Reject the transfer out',
    rejectOutTitle: 'Reject the request to transfer the domain away',
    rejectOutDescription: 'A transfer can only be refused on one of the grounds the transfer policy allows. The ground is sent to the registry.',
    rejectOutSubmit: 'Reject the transfer',
    rejectedOut: 'Rejected at the registry: the domain stays.',
    respondPending: 'Sent: the registry has not confirmed the answer yet.',
    respondAwaiting: 'The domain is protected: the request is recorded and waits for confirmation by authorised staff.',
    ground: 'Ground for rejecting',
    groundPlaceholder: 'Choose a ground',
    grounds: {
      EVIDENCE_OF_FRAUD: 'Evidence of fraud',
      UDRP_OR_URS_ACTION: 'Pending UDRP / URS dispute',
      COURT_ORDER: 'Court order',
      IDENTITY_DISPUTE: 'Dispute over the identity of the requester',
      PAYMENT_DISPUTE: 'Payment dispute',
      EXPRESS_REGISTRANT_OBJECTION: 'The registrant objects to the transfer',
      WITHIN_POST_REGISTRATION_OR_TRANSFER_LOCK: 'Within the lock period after registration or transfer',
    },
    other: 'Other registrar',
    customer: 'Customer',
    registry: 'Registry',
    lifecycle: 'Domain lifecycle',
    requested: 'Requested',
    requestedBy: 'Requested by',
    registryPending: 'Pending at registry since',
    autoApproveAt: 'Registry auto-approves',
    decidedAt: 'Decided',
    decidedBy: 'Decided by',
    completedAt: 'Completed',
    rejectReason: 'Reason for rejection',
    cancelledByUs: 'Cancelled by us (the gaining registrar): {{reason}}',
    authInfo: 'AuthInfo on file',
    authInfoYes: 'Yes (encrypted, never shown)',
    authInfoNo: 'No',
    follow: 'Workflow and registry command',
    workflow: 'Workflow',
    step: 'current step: {{step}}',
    command: 'Registry command',
    order: 'Order item',
    noOrder: 'No order item; the transfer was not billed through this platform.',
    evidence: 'Authorisation',
    evidenceDescription: 'How the transfer was authorised. Personal identifiers are never stored here.',
    noEvidence: 'No authorisation recorded',
    events: 'Related history',
    noEvents: 'No domain events for this transfer',
    openHistory: 'Open in domain history',
  },

  changes: {
    title: 'Registrant changes',
    subtitle: 'Requests to hand a domain to a new registrant, with both confirmations and the new registrant’s verification.',
    crumb: 'Business',
    filterDomain: 'Domain starts with',
    filterDomainPlaceholder: 'e.g. example',
    filterStatus: 'Status',
    anyStatus: 'Any status',
    search: 'Search',
    tableTitle: 'Registrant changes',
    tableDescription: 'Newest first. Click a domain to open the request: both registrants, their confirmations and the registry command.',
    colDomain: 'Domain',
    colFrom: 'Current registrant',
    colTo: 'New registrant',
    colStatus: 'Status',
    colCustomer: 'Customer',
    colRequested: 'Requested',
    lockOptOut: 'no 60-day lock',
    empty: 'No registrant changes match',
    emptyBody: 'Try another status or reset the filters.',
    shown: '{{count}} shown on this page',

    detailTitle: 'Registrant change: {{domain}}',
    detailSubtitle: 'Handing {{domain}} to a new registrant: confirmations, verification and registry command.',
    notFound: 'Registrant change not found',
    loadFailed: 'Could not load the registrant change',
    overview: 'Request',
    domain: 'Domain',
    status: 'Status',
    customer: 'Customer',
    registry: 'Registry',
    requested: 'Requested',
    requestedBy: 'Requested by',
    completedAt: 'Closed',
    failure: 'Outcome',
    optOut: '60-day transfer lock',
    optOutYes: 'Waived by the new registrant',
    optOutNo: 'Applies after the change',
    from: 'Current registrant',
    to: 'New registrant',
    version: 'Version',
    country: 'Country',
    verification: 'Verification',
    verifiedAt: 'Verified',
    verificationExpires: 'Valid until',
    method: 'Method',
    newDossier: 'New dossier',
    noNewDossier: 'Not created yet',
    follow: 'Workflow and registry command',
    workflow: 'Workflow',
    step: 'current step: {{step}}',
    command: 'Registry command',
    noCommand: 'No registry command yet',
    evidence: 'Confirmations',
    evidenceDescription: 'Who confirmed the change, how and when. Personal identifiers are never stored here.',
    events: 'Related history',
    noEvents: 'No domain events for this change',
  },

  evidence: {
    method: 'Method',
    confirmedAt: 'Confirmed',
    notConfirmed: 'Not confirmed yet',
    actorType: 'Confirmed by',
    agreementVersion: 'Agreement version',
    channel: 'Channel',
    side: { current_registrant: 'Current registrant', new_registrant: 'New registrant', gaining_registrar: 'Gaining registrar', losing_registrar: 'Losing registrar' },
  },

  events: {
    when: 'When',
    event: 'Event',
    actor: 'By',
    correlation: 'Correlation',
  },

  compliance: {
    decisions: 'Policy decisions',
    decisionsDescription: 'Every compliance decision taken for this domain, with the policy versions evaluated.',
    noDecisions: 'No decisions recorded',
    decided: 'Decided',
    operation: 'Operation',
    outcome: 'Outcome',
    actions: 'Required actions / block reasons',
    policies: 'Policies',
    correlation: 'Correlation',
    abuse: 'Abuse cases',
    openAbuse: 'Open in Abuse',
    noAbuse: 'No abuse cases',
    abuseLine: '{{category}} · {{priority}} priority · opened {{when}}',
    disputes: 'Disputes and legal locks',
    openDisputes: 'Open in Disputes',
    noDisputes: 'No disputes',
    lockActive: 'active',
    lockReleased: 'released',
    lockLine: '{{lock}} · {{state}}',
    categories: {
      SPAM: 'Spam',
      PHISHING: 'Phishing',
      MALWARE: 'Malware',
      BOTNET: 'Botnet',
      PHARMING: 'Pharming',
      FRAUD: 'Fraud',
      CSAM: 'Child sexual abuse material',
      TRADEMARK: 'Trademark infringement',
      OTHER: 'Other',
    },
    priorities: { LOW: 'low', MEDIUM: 'medium', HIGH: 'high', CRITICAL: 'critical' },
    disputeTypes: {
      UDRP: 'UDRP',
      URS: 'URS',
      COURT_ORDER: 'Court order',
      REGISTRY_ACTION: 'Registry action',
      GOVERNMENT_REQUEST: 'Government request',
      OTHER: 'Other dispute',
    },
    requiredActions: {
      CREATE_DOSSIER: 'Create the domain dossier',
      COMPLETE_DOSSIER: 'Complete the domain dossier',
      VERIFY_SUBJECT_IDENTITY: 'Verify the registrant’s identity',
      PROVIDE_DOCUMENT: 'Provide a document',
      UPLOAD_DOCUMENT: 'Provide a document',
    },
    blockReasons: {
      REGISTRANT_TYPE_NOT_ELIGIBLE: 'Registrant type not eligible',
      NATIONALITY_NOT_ELIGIBLE: 'Nationality not eligible',
      COUNTRY_NOT_ELIGIBLE: 'Country not eligible',
      SUBJECT_MISSING: 'No registrant on file',
      LEGAL_LOCK: 'Legal lock in force',
    },
    acceptedMethods: 'accepted: {{methods}}',
    underPolicy: 'under {{policy}}',
  },

  vnnic: {
    title: 'VNNIC',
    description: 'VNNIC obligations for this domain.',
    program: 'Program',
    programVn: 'Dossier sync (.vn)',
    programIntl: 'International domain report (0805A / 0805B), if the registrant is Vietnamese',
    dossier: 'Dossier',
    openDossier: 'Open the dossier',
    note: 'Sync status and reports for all domains are on the VNNIC pages below.',
    dossierSync: 'Dossier sync',
    intlReports: 'International reports',
    coverage: 'Coverage / mismatch',
    sync: 'Dossier sync with VNNIC',
    syncDescription: 'The latest submission of this domain’s dossier and VNNIC’s answer.',
    syncLoading: 'Loading sync state…',
    syncFailed: 'Could not load the sync state',
    noSync: 'This domain’s dossier has not been sent to VNNIC yet.',
    syncStatus: 'Sync state',
    syncOperation: 'Submission',
    operations: { SUBMIT: 'First submission', UPDATE: 'Dossier update' },
    dossierVersion: 'Dossier version',
    attempts: 'Attempts',
    reference: 'VNNIC reference',
    changedAt: 'State since',
    nextAttempt: 'Next attempt',
    lastAttempt: 'Latest attempt',
    attemptResult: 'Result',
    submittedAt: 'Sent',
    completedAt: 'Answered',
    errorCode: 'Reason',
    errorMessage: 'VNNIC’s message',
  },

  history: {
    requestedBy: 'requested by',
    queue: {
      title: 'Pending operations',
      loading: 'Loading the queue…',
      empty: 'No operation is waiting.',
      running: 'Running',
      step: 'Step',
      next: 'Next run {{when}}',
      scheduledBadge: 'Scheduled',
      scheduled: 'Runs {{when}}',
      actions: {
        DOMAIN_RENEW_FLAG_RELEASE: 'Remove clientRenewProhibited (24 hours after renewal)',
        DOMAIN_RECONCILE: 'Read the domain back at the registry',
      },
    },
    title: 'History',
    description: 'Every domain event in order, protected by a hash chain.',
    loading: 'Loading history…',
    loadFailed: 'Could not load history',
    filter: 'Filter',
    filterPlaceholder: 'Event type or correlation ID',
    intact: 'Hash chain intact: every event’s hash matches its content and its predecessor.',
    broken: 'Hash chain broken at {{count}} event(s)',
    brokenBody: 'The history may have been altered outside the application. Escalate to security; do not edit the rows.',
    noEvents: 'No events',
    noEventsFiltered: 'No event matches this filter. Clear the filter to see every event.',
    mismatch: 'Hash mismatch',
    by: 'by {{actor}}',
    via: 'via {{source}}',
    payload: 'Payload',
    correlation: 'Correlation',
    workflow: 'Workflow',
    registryTx: 'Registry command',
    registryTxTrid: 'Registry command {{cltrid}}',
    request: 'Request',
    chainProblem: {
      SEQUENCE_GAP: 'an event is missing before this one (gap in the sequence)',
      PREVIOUS_HASH_MISMATCH: 'the link to the previous event does not match',
      EVENT_HASH_MISMATCH: 'the stored hash does not match the event content',
      other: 'the hash check failed',
    },
    olderFailed: 'Could not load older events',
    loadOlder: 'Load older events',
    loadingOlder: 'Loading…',
  },

  audit: {
    title: 'Audit log',
    description: 'Every audited action on this domain, newest first.',
    loadFailed: 'Could not load the audit log',
    empty: 'No audit entries for this domain',
    when: 'When',
    action: 'Action',
    actor: 'By',
    outcome: 'Outcome',
    reason: 'Reason',
    correlation: 'Correlation',
    outcomes: { SUCCESS: 'Success', DENIED: 'Denied', FAILED: 'Failed', FAILURE: 'Failure', ERROR: 'Error' },
    older: 'Older entries',
  },

  observability: {
    severities: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', CRITICAL: 'Critical' },
    issueTypes: {
      UNKNOWN_OUTCOME: 'Registry command outcome unknown',
      EXISTENCE: 'Domain exists on one side only',
      SPONSORSHIP: 'Sponsoring registrar differs',
      NAMESERVER: 'Nameservers differ from the registry',
      EXPIRY: 'Expiry date differs from the registry',
      STATUS: 'Statuses differ from the registry',
    },
    jump: 'Jump to',
    jumpDescription: 'Log queries that name a domain need a stated reason; it is recorded with the query.',
    logsMentioning: 'Logs mentioning {{name}}',
    latestLogs: 'Logs of the latest operation',
    registryHealth: '{{registry}} health',
    loading: 'Loading operational links…',
    loadFailed: 'Could not load operational links',
    issues: 'Open reconciliation issues',
    noIssues: 'No open reconciliation issues.',
    detected: 'detected {{when}}',
    workflows: 'Workflows',
    noWorkflows: 'No workflows',
    updated: 'Updated',
    workflow: 'Workflow',
    state: 'State',
    step: 'Step',
    correlation: 'Correlation',
    txs: 'Registry commands',
    noTxs: 'No registry commands',
    started: 'Started',
    operation: 'Command',
    events: 'Recent events',
    noEvents: 'No events',
    when: 'When',
    event: 'Event',
    audited: 'Every look-up on this tab is recorded in the query audit.',
  },

  subject: {
    sync: {
      title: 'Domains using this subject',
      description: 'Contacts of the domains cloned from this subject. Editing the subject does not change domains by itself: choose the domains to sync.',
      loadFailed: 'Could not load the domains',
      empty: 'No domain uses this subject yet',
      domain: 'Domain',
      contacts: 'Contacts',
      changes: 'Differences',
      statuses: { CURRENT: 'Up to date', BEHIND: 'Older details', EDITED: 'Edited separately', SYNCING: 'Syncing' },
      select: 'Select {{domain}}',
      action: 'Sync to domains',
      actionCount: 'Sync to {{count}} domains',
      dialogTitle: 'Sync to domains',
      dialogDescription:
        'The chosen domains’ contacts take the subject’s current details; each domain is updated at the registry on its own. If the name or ID number changed, the domain’s registrant is changed through a registrant change or name-change request.',
      editedWarning: 'Some chosen domains have contacts edited separately: syncing replaces them with the subject’s details.',
      submit: 'Sync',
      results: {
        STARTED: 'Updating {{roles}}',
        REGISTRANT_CHANGE: 'Registrant change opened',
        PENDING_CONFIRMATION: 'Waiting for staff confirmation',
        NAME_CHANGE: 'Name-change request opened',
        UP_TO_DATE: 'Already up to date',
        SKIPPED: 'Not done: {{reason}}',
      },
    },
    readiness: {
      title: 'Ready to register domains',
      description: 'What the subject’s current data lacks under the rules for every domain, then what a registry or upstream registrar needs beyond them: the same check as quote and order. The subject’s verification is not checked at order: the order waits in "Pending identity" until the subject is verified.',
      missing: '{{count}} missing',
      ready: 'The subject has everything needed to register domains.',
      missingTitle: 'Still missing to register domains',
      extraFor: '{{name}} also needs:',
      verification: {
        label: 'Subject verification',
        EKYC: 'An individual is verified by eKYC only',
        SUBJECT_DECLARATION: 'An organisation is verified by its subject declaration (digitally signed, or on paper, signed and stamped)',
        states: {
          MISSING: 'Not verified',
          NOT_ACCEPTED: 'Not verified (eKYC needed): a signed declaration no longer verifies an individual',
          SIGN_AGAIN: 'The data changed: the subject declaration must be signed again',
          PENDING: 'The subject declaration is waiting for its check or a staff decision',
        },
        open: 'Open the subject declaration',
      },
    },
    statuses: { ACTIVE: 'Active', INACTIVE: 'Inactive', SUSPENDED: 'Suspended', MERGED: 'Merged', DELETED: 'Deleted' },
    types: {
      INDIVIDUAL: 'Individual',
      ORGANIZATION: 'Organisation',
      ORGANISATION: 'Organisation',
      BUSINESS: 'Business',
      HOUSEHOLD_BUSINESS: 'Household business',
      GOVERNMENT_ENTITY: 'Government body',
      FOREIGN_ENTITY: 'Foreign organisation',
      OTHER: 'Other',
    },
    verificationStatuses: { VERIFIED: 'Verified', PENDING: 'Pending', IN_PROGRESS: 'In progress', FAILED: 'Failed', REJECTED: 'Rejected', EXPIRED: 'Expired' },
    identifierTypes: {
      NATIONAL_ID: 'Citizen ID (CCCD)',
      TAX_CODE: 'Tax code',
      ENTERPRISE_CODE: 'Enterprise code',
      HOUSEHOLD_BUSINESS_CODE: 'Household business registration number',
      BUSINESS_HOUSEHOLD_TAX_CODE: 'Household business tax code',
      PASSPORT: 'Passport',
      CCCD: 'Citizen ID (CCCD)',
      CMND: 'ID card (CMND)',
      BUSINESS_REGISTRATION: 'Business registration',
      TAX_ID: 'Tax ID',
      BIRTH_DATE: 'Date of birth',
    },
    genders: { MALE: 'Male', FEMALE: 'Female' },
    relationships: {
      LEGAL_REPRESENTATIVE: 'Legal representative',
      GUARDIAN: 'Guardian',
      AUTHORIZED_REPRESENTATIVE: 'Authorised representative',
      PARENT_ORGANIZATION: 'Parent organisation',
      OTHER: 'Other',
    },
    addressTypes: { LEGAL: 'Legal address', POSTAL: 'Postal address', RESIDENTIAL: 'Residence', OTHER: 'Other address' },
    profile: {
      title: 'Stored data',
      description: 'Everything stored for the current version. The birth date and identity numbers are masked; revealing them needs a reason and is recorded in the audit log.',
      personal: 'Person',
      organisation: 'Organisation',
      contact: 'Contact',
      address: 'Address',
      attribute: {
        first_name: 'First name',
        last_name: 'Last name',
        contact_first_name: 'Contact first name',
        contact_last_name: 'Contact last name',
        owner_name: 'Household business owner',
        household_code: 'Household business code',
        id_document_issuer: 'Document issued by',
      },
      birthDate: 'Date of birth',
      birthDateMissing: 'Not on file',
      gender: 'Gender',
      position: 'Position',
      tradeName: 'Trading name',
      street: 'House number and street',
      ward: 'Ward/commune',
      province: 'Province/city',
      city: 'City',
      state: 'State/province',
      postalCode: 'Postal code',
      fullAddress: 'Full address',
      email: 'E-mail',
      phone: 'Phone',
      fax: 'Fax',
      issuedAt: 'issued {{date}}',
      issuedBy: 'issued in: {{country}}',
      expiresAt: 'expires {{date}}',
      otherAttributes: 'Other stored data',
      relationships: 'Relationships',
      since: 'since {{date}}',
      versionReason: 'Reason for this version',
      none: 'None',
    },
    methods: {
      EKYC: 'eKYC (VNNIC Cloud eKYC)',
      DIGITAL_SIGNATURE: 'Digitally signed documents',
      EKYC_CCCD_CHIP: 'eKYC with chip ID card',
      BUSINESS_REGISTRY_LOOKUP: 'Business registry lookup',
      MANUAL_REVIEW: 'Manual review',
      VIDEO_CALL: 'Video call',
    },
    loading: 'Loading subject…',
    notFound: 'Subject not found',
    loadFailed: 'Could not load the subject',
    fallbackTitle: 'Subject',
    description: 'The legal registrant behind the domain. Identity numbers are masked; revealing one is audited.',
    type: 'Type',
    status: 'Status',
    id: 'Subject ID',
    currentVersion: 'Current version',
    country: 'Country',
    nationality: 'Nationality',
    managingCustomer: 'Managing customer',
    identifiers: 'Identifiers',
    identifiersDescription: 'Shown masked. Reveal needs a reason and is recorded in the audit log.',
    noIdentifiers: 'No identifiers on the current version.',
    verifications: 'Verifications',
    noVerifications: 'No verifications',
    noVerificationsBody: 'Identity verification has not been run for this subject yet.',
    method: 'Method',
    verified: 'Verified',
    expires: 'Expires',
    policy: 'Policy',
    requestId: 'Request ID',
    versionCol: 'For version',
    versions: 'Version history',
    version: 'Version',
    legalName: 'Legal name',
    changeReason: 'Change reason',
    changeReasons: { INITIAL: 'First version', NAME_CHANGE: 'Name change', CORRECTION: 'Correction', ADDRESS_CHANGE: 'Address change', CREATED_BY_STAFF: 'Created by staff', EKYC: 'eKYC', DIGITAL_SIGNATURE: 'Digital signature' },
    created: 'Created',
    domains: 'Domains held',
    domainsDescription: 'Domains whose current registrant is this subject.',
    noDomains: 'This subject holds no domains.',
    domain: 'Domain',
    lifecycle: 'Lifecycle',
    dossierState: 'Dossier',
    expiry: 'Registry expiry',
    customer: 'Customer',
    dossiers: 'Dossiers',
    noDossiers: 'No dossiers name this subject.',
    dossierVersion: 'Version',
    dossierType: 'Type',
    dossierStatus: 'Status',
    subjectVersion: 'Registrant version',
    completed: 'Completed',
    contacts: 'Registry contacts',
    contactsDescription: 'Contact objects at the registries created from this subject.',
    noContacts: 'No registry contact objects were created from this subject.',
    contact: 'Contact',
    registry: 'Registry',
    roles: 'Current roles',
    contactDomains: 'Domains',
    pageTitle: 'Subject',
    pageSubtitle: 'Registrant details, verifications, domains and version history.',
    crumbBusiness: 'Business',
    crumbSubjects: 'Subjects',
    docTitle: 'Subject {{name}}',
  },

  registryTx: {
    openTrace: 'Open the trace',
    title: 'Registry command',
    docTitle: 'Registry command {{operation}}',
    crumbSection: 'Registry Ops',
    crumbList: 'Registry Transactions',
    notFound: 'Registry command not found',
    loadFailed: 'Could not load the registry command',
    subtitle: 'One EPP command sent to {{registry}}: its identifiers, outcome and where to follow it.',
    unresolvedTitle: 'Outcome not known yet',
    unresolved: 'Reconciliation resolves it against registry state; do not re-send the command by hand.',
    command: 'Command',
    state: 'State',
    attempt: 'Attempt',
    retryOf: 'Retry of',
    retryOfLink: 'the previous attempt',
    object: 'Object',
    domain: 'Domain',
    result: 'Result',
    errorClass: 'Error class',
    trafficClass: 'Traffic class',
    registry: 'Registry',
    started: 'Started',
    sent: 'Sent',
    completed: 'Completed',
    latency: 'Latency',
    none: 'none',
    id: 'Command ID',
    follow: 'Follow it',
    followDescription: 'Jump to the workflow, correlated logs and traces.',
    correlation: 'Correlation',
    workflow: 'Workflow',
    request: 'Request',
    logs: 'Logs for this operation',
    traces: 'Traces for this operation',
    eppLogs: 'EPP command logs for the clTRID',
    payloads: 'EPP exchange',
    withheld: 'Hidden. Viewing the redacted EPP exchange needs a separate permission, and every view is recorded.',
    redacted: 'Stored already redacted. This view was recorded in the audit log.',
    requestPayload: 'Request',
    responsePayload: 'Response',
    payloadsDescription:
      'The request and response as sent and received (passwords, AuthInfo and identity numbers replaced), read from the log store by clTRID and decrypted on the server. Every view is recorded in the audit log.',
    summary: 'Summary kept with the command',
    exchange: {
      title: 'As exchanged',
      loading: 'Reading the log store…',
      loadFailed: 'Could not read the exchange from the log store',
      storeOff: 'The log store is not connected yet, so the exchanged text cannot be shown. The summary above is still available.',
      reasonIntro: "The exchanged text holds the registrant's contact data. State why you are viewing it; the reason is audited with the view.",
      mfa: 'Viewing the exchanged text needs a session signed in with MFA. Sign in again with MFA.',
      expiredTitle: 'Past the retention period',
      expired: 'Registry exchange logs are kept {{days}} days; this command ran {{age}} days ago, so its exchange has been cleaned up.',
      retentionSetting: 'Log retention settings',
      noneTitle: 'No exchange logged for this clTRID',
      none: 'The command ran before exchange logging was switched on, was never sent, or its log has not reached the log store yet.',
      request: 'Request',
      response: 'Response',
      noResponse: 'No readable response (timed out, connection lost or not sent).',
      copy: 'Copy as exchanged',
      copied: 'Copied',
      session: 'Session',
      at: 'Logged',
      result: { success: 'Succeeded', rejected: 'Rejected', unknown: 'Outcome unknown', error: 'Not sent' },
      payload: {
        absent: 'Nothing was logged for this exchange.',
        no_field_key: 'The text is not available: the service had no field-encryption key when it wrote or read the log.',
        too_large: 'The exchange was too large for one log line, so its text was not kept.',
        seal_failed: 'The text could not be encrypted when it was logged, so it was not kept.',
        unreadable: 'The text could not be decrypted (the key changed or the log line is damaged).',
      },
    },
  },

  search: {
    title: 'Search',
    docTitle: 'Search: {{q}}',
    subtitle: 'Look up any domain, customer, registrant or identifier across the platform.',
    empty: 'Type in the search box above.',
    interpreted: 'Interpreted as',
    interpretedHint: 'How the query was read from its shape, with shortcuts for each reading.',
    confidence: { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' },
    results: 'Results',
    searching: 'Searching…',
    failed: 'Search failed',
    noMatches: 'No matches',
    noMatchesBody: 'Nothing matched {{q}}. IDs, clTRIDs, svTRIDs and contact IDs must match exactly; names need at least 3 characters.',
    correlation: 'If this is a correlation ID, the {{link}} shows every event, workflow, registry command and audit row it touched.',
    correlationLink: 'Correlation view',
    accepts: 'Search accepts',
    acceptsList: {
      domains: 'Domain names, in Unicode (ví-dụ.vn) or Punycode (xn--v-d-rma6749a.vn)',
      names: 'Customer and registrant names (3 characters or more)',
      contacts: 'Registry contact IDs, such as TINO-C123456',
      trids: 'EPP clTRID / svTRID',
      ids: 'Record, request, correlation, trace and workflow IDs',
    },
    shortcut: 'Press {{key}} anywhere to focus the search box.',
    kinds: {
      domain: 'Domain',
      domain_event: 'Event',
      registry_transaction: 'Registry command',
      workflow: 'Workflow',
      subject: 'Registrant',
      customer: 'Customer',
      transfer: 'Transfer',
      registrant_change: 'Registrant change',
      dossier: 'Dossier',
      contact: 'Contact',
    },
    hit: {
      domainState: 'Domain · {{state}}',
      event: '{{domain}} · history event matched by request or correlation ID',
      registryTx: 'Registry command · {{detail}}',
      workflow: 'Workflow · {{detail}}',
      subject: 'Registrant · {{type}}',
      subjectHidden: 'Registrant ({{type}}), name hidden',
      customer: 'Customer · {{type}}',
      transfer: '{{direction}} · {{status}}',
      registrantChange: 'Registrant change · {{status}}',
      dossier: 'Dossier {{version}} · {{status}}',
      contact: 'Contact at {{registry}}',
    },
  },
  /** "Tra cứu tên miền" (docs/specs/external-lookup.md). */
  lookup: {
    title: 'Domain lookup',
    subtitle: 'Is a name free in several TLDs at once, and who holds it: our registry connections first, then the registries’ public WHOIS / RDAP servers.',
    label: 'Name or label',
    labelHint: 'A label (tino) is checked in every chosen TLD; a full name (tino.vn) adds its TLD.',
    tlds: 'TLDs',
    tldsHint: 'Separated by spaces or commas, at most 50 names in all.',
    submit: 'Look up',
    stop: 'Stop',
    running: 'Looking up… results appear as soon as each source answers.',
    tooMany: 'At most 50 names per lookup.',
    empty: 'Type a name and press Look up.',
    col: { name: 'Name', status: 'Result', source: 'Source', age: 'Answer', time: 'Time', actions: '' },
    status: { available: 'Available', registered: 'Registered', unknown: 'Unknown', confirming: 'Registered (confirming…)' },
    source: { REGISTRY: 'Registry (Tino connection)', VNNIC_WHOIS_API: 'VNNIC WHOIS', RDAP: 'RDAP', WHOIS: 'WHOIS', DNS: 'DNS (delegation)' },
    authoritative: 'Authoritative',
    publicSource: 'Public server',
    cached: 'From cache, {{age}} old',
    fresh: 'Fresh',
    premium: 'Premium: {{amount}} {{currency}}',
    reason: {
      INVALID_NAME: 'Not a valid domain name',
      TLD_NOT_SUPPORTED: 'TLD not in the catalogue',
      SOURCE_NOT_CONFIGURED: 'VNNIC WHOIS is not configured on this server (no certificate imported)',
      NO_SOURCE: 'No source for this TLD (lookup disabled or no public server)',
      TIMEOUT: 'The source did not answer in time',
      RATE_LIMITED: 'Too many lookups at this server; try again shortly',
      SOURCE_ERROR: 'The source answered with an error',
      DELEGATED: 'Delegated in DNS',
      RDAP_NOT_FOUND: 'RDAP: not found',
      WHOIS_NOT_FOUND: 'WHOIS: no match',
    },
    notice: 'Answers from public servers (RDAP, WHOIS, VNNIC WHOIS) are not a confirmation that the name can be registered: it may be reserved, premium or blocked. Only our registry connection is authoritative.',
    openRecord: 'WHOIS / RDAP',
    record: {
      notConfigured: {
        title: 'VNNIC WHOIS is not configured',
        body: 'Registration details of .vn names come only from the VNNIC WHOIS service. This server has no VNNIC WHOIS certificate yet, so they cannot be looked up.',
        action: 'Import the VNNIC WHOIS certificate',
      },
      title: 'Registration record of {{name}}',
      source: 'Source',
      server: 'Server',
      found: 'Registered',
      notFound: 'The source says this name is not registered.',
      status: 'Status',
      registrar: 'Registrar',
      created: 'Created',
      expires: 'Expires',
      nameservers: 'Name servers',
      dnssec: 'DNSSEC',
      registrant: 'Registrant',
      registrantHidden: 'Hidden: seeing registrant names needs the permission “See registrant names in registry WHOIS lookups”. Registrant names are never stored.',
      raw: 'Answer as received',
      cached: 'From cache ({{age}})',
      failed: 'The record could not be read',
    },
    commandItem: 'Look up “{{q}}” in the TLDs',
  },
  extra: {
    heading: 'Additional information required for .{{zone}}',
    intro: 'This extension asks for more than the registrant’s usual details. Fill them in for this domain.',
    required: 'required',
    requiredIndividuals: 'required for individuals',
    requiredOrganizations: 'required for organisations',
    optional: 'optional',
    choose: 'Choose…',
    yes: 'Yes',
    no: 'No',
    fromSubject: 'Leave empty to use the registrant’s details ({{hint}}).',
    fromSubjectPlain: 'Filled in from the registrant’s details.',
    fromEarlier: 'Filled in with what this registrant gave for another .{{zone}} domain.',
    fromEarlierHidden: 'Leave empty to reuse what this registrant gave before ({{hint}}).',
    missing: 'Still to fill in: {{fields}}',
    loadFailed: 'The additional information of this extension could not be loaded.',
    sectionHint: 'Kept for this domain only and sent to the registrar with the registrant’s details.',
    notSet: 'Not given',
    hidden: 'hidden',
    sources: {
      GIVEN: 'Entered',
      EARLIER: 'From the registrant’s other domain',
      SUBJECT: 'From the registrant’s details',
    },
    by: {
      OPERATOR: 'staff',
      CUSTOMER: 'customer',
      RESELLER: 'reseller',
      SYSTEM: 'system',
      EXTERNAL: 'external',
    },
    edit: 'Edit',
    editTitle: 'Additional information of {{domain}}',
    editDescription:
      'Applies to this domain only. New values go to the registrar with the registrant’s details the next time they are sent (a registration still to be sent or retried, a registrant change); details already at the registrar do not change by themselves.',
    keepHidden: 'Leave empty to keep the current value.',
    save: 'Save',
    saved: 'Saved.',
    todoLink: 'Enter the additional information',
  },
} as const;
